// Getting data in: CSV / text files, pasted tables, Excel workbooks (any sheet, or the
// scanline field template), project files, the sample, and drag-and-drop anywhere.
// GPL-3.0-or-later.
import * as S from "@dipfield/stereo";
import { t, tn } from "../../i18n/index.js";
import { $, esc } from "../../lib/dom.js";
import { fmt } from "../../lib/format.js";
import { toast } from "../../lib/toast.js";
import type { Sheet } from "../../lib/xlsx.js";
import { pushHistory } from "../../state/store.js";
import { applyImport, applySample, applyTemplate } from "../../state/ops.js";
import { openProjectText } from "../../shell/files.js";
import { X, P, on, update } from "./ctx.js";

let pending: S.Table | null = null;
let pendingTemplate: S.ScanlineImport | null = null;
let pendingSheets: Sheet[] = [];
let hideLeft: (h: boolean) => void = () => {};

/** An import issue in the UI language (codes from stereo csv.ts). */
export function issueText(i: S.ParseIssue): string {
  const v = i.values ?? [];
  switch (i.code) {
    case "not-number": return t("row {r}: not a number", { r: i.row });
    case "out-of-range": return t("row {r}: {a}, {b} is out of range for this format", { r: i.row, a: v[0], b: v[1] });
    case "scanline-orientation": return t("row {r}: scanlines, invalid trend/plunge for {id}", { r: i.row, id: esc(v[0]) });
    case "measurement-orientation": return t("row {r}: measurements, invalid dip/dip direction", { r: i.row });
    case "unknown-scanline": return t("row {r}: measurements, {id} is not in the scanlines sheet (counted as spot)", { r: i.row, id: esc(v[0]) });
    default: return `${i.row}: ${esc(i.reason)}`;
  }
}
const issueList = (issues: S.ParseIssue[], max: number) => issues.slice(0, max).map(issueText).join(", ") + (issues.length > max ? " …" : "");

function openText(text: string, name: string) { openTable(S.parseTable(text), name); }
function openTable(table: S.Table, name: string) {
  pendingTemplate = null; $("imp-tpl").hidden = true;
  pending = table;
  if (!pending.rows.length) { $("imp-msg").textContent = t("No data found in {name}", { name }); $("imp").hidden = false; $("mapping").hidden = true; return; }
  const m = S.guessMapping(pending);
  const ncol = Math.max(...pending.rows.slice(0, 50).map((r) => r.length));
  const names = Array.from({ length: ncol }, (_, i) => pending!.header?.[i] || t("Column {n}", { n: i + 1 }));
  const opts = (sel: number | null | undefined, none = false) =>
    (none ? `<option value="">${t("Not used")}</option>` : "") + names.map((n, i) => `<option value="${i}" ${i === sel ? "selected" : ""}>${esc(n)}</option>`).join("");
  $<HTMLSelectElement>("m-conv").value = m.convention;
  $("m-a").innerHTML = opts(m.colA); $("m-b").innerHTML = opts(m.colB); $("m-w").innerHTML = opts(m.weightCol, true); $("m-t").innerHTML = opts(m.traverseCol, true);
  $("imp-src").textContent = `${name} · ${tn(pending.rows.length, "{n} row", "{n} rows")}${pending.header ? " " + t("(with a header row)") : ""}`;
  $("mapping").hidden = false; $("imp").hidden = false;
  preview();
}
function openTemplate(tp: S.ScanlineImport, name: string) {
  pending = null; pendingTemplate = tp; $("sheet-row").hidden = true;
  $("mapping").hidden = true; $("imp-tpl").hidden = false; $("imp").hidden = false;
  $("imp-src").textContent = `${name} · ${t("scanline template")}`;
  const total = tp.scanlines.reduce((a, s) => a + (s.length ?? 0), 0);
  $("imp-tpl-info").innerHTML = t("{s} scanlines{len} and {n} measurements. A linear traverse is created automatically for each line from its trend/plunge.",
    { s: tp.scanlines.length, len: total ? ` (${t("{m} m in total", { m: fmt(total, 1) })})` : "", n: tp.planes.length });
  $("imp-go").textContent = t("Import {n} rows", { n: tp.planes.length }); $<HTMLButtonElement>("imp-go").disabled = !tp.planes.length;
  $("imp-msg").innerHTML = tp.issues.length ? t("{n} rows skipped or flagged:", { n: tp.issues.length }) + " " + issueList(tp.issues, 4) : t("Every row was read");
}
async function openXlsx(buf: ArrayBuffer, name: string) {
  let sheets: Sheet[];
  try { const { readXlsx } = await import("../../lib/xlsx.js"); sheets = await readXlsx(buf); }
  catch (err) { toast(t("Could not read the .xlsx file: {e}", { e: esc((err as Error).message) })); return; }
  const tp = S.parseScanlineTemplate(sheets);
  if (tp) return openTemplate(tp, name);
  pendingSheets = sheets.filter((sh) => sh.rows.some((r) => r.some((c) => c !== "")));
  if (!pendingSheets.length) { toast(t("No data found in {name}", { name: esc(name) })); return; }
  $("sheet-row").hidden = pendingSheets.length < 2;
  $("m-sheet").innerHTML = pendingSheets.map((sh, i) => `<option value="${i}">${esc(sh.name)}</option>`).join("");
  openTable(S.tableFromRows(pendingSheets[0].rows), `${name} › ${pendingSheets[0].name}`);
}
function mapping(): S.Mapping {
  const w = $<HTMLSelectElement>("m-w").value, tv = $<HTMLSelectElement>("m-t").value;
  return { convention: $<HTMLSelectElement>("m-conv").value as S.Convention, colA: +$<HTMLSelectElement>("m-a").value, colB: +$<HTMLSelectElement>("m-b").value,
    weightCol: w === "" ? null : +w, traverseCol: tv === "" ? null : +tv };
}
function preview() {
  if (!pending) return;
  const L = S.FORMAT_LABELS[$<HTMLSelectElement>("m-conv").value as S.OrientationFormat];
  $("m-a-label").textContent = t("Column {name}", { name: L[1] }); $("m-b-label").textContent = t("Column {name}", { name: L[2] });
  const { planes, issues } = S.toPlanes(pending, mapping());
  $("imp-go").textContent = t("Import {n} rows", { n: planes.length });
  $<HTMLButtonElement>("imp-go").disabled = !planes.length;
  $("imp-msg").innerHTML = issues.length ? t("{n} rows skipped:", { n: issues.length }) + " " + issueList(issues, 5) : t("Every row was read");
}
export async function importFile(f: File) {
  if (/\.xlsx$/i.test(f.name)) return openXlsx(await f.arrayBuffer(), f.name);
  const text = await f.text();
  if (/\.json$/i.test(f.name) || /^\s*\{/.test(text)) return openProjectText(text, f.name);
  $("sheet-row").hidden = true;
  openText(text, f.name);
}
const done = (n: number) => { toast(t("Imported {n} rows · the data panel is hidden; press Data to open it", { n })); hideLeft(true); };

export function mountImporter(setLeftHidden: (h: boolean) => void) {
  hideLeft = setLeftHidden; pending = null; pendingTemplate = null; pendingSheets = [];
  on($("m-sheet"), "change", (e: Event) => {
    const sh = pendingSheets[+(e.target as HTMLSelectElement).value];
    if (sh) openTable(S.tableFromRows(sh.rows), t("sheet {name}", { name: sh.name }));
  });
  ["m-conv", "m-a", "m-b", "m-w", "m-t"].forEach((id) => on($(id), "change", preview));
  on($("imp-go"), "click", () => {
    if (pendingTemplate) {
      const tp = pendingTemplate; pushHistory();
      applyTemplate(P(), tp, $<HTMLInputElement>("imp-tw").checked, t("Unassigned (spot)"));
      X.selectedSet = -1; X.tablePage = 0;
      $("imp").hidden = true; pendingTemplate = null; update(); done(P().data.planes.length); return;
    }
    if (!pending) return;
    const map = mapping(), { planes } = S.toPlanes(pending, map);
    pushHistory();
    applyImport(P(), planes, map.convention, $<HTMLInputElement>("imp-replace").checked);
    $("imp").hidden = true; pending = null; update(); done(planes.length);
  });
  on($("imp-cancel"), "click", () => { $("imp").hidden = true; pending = null; pendingTemplate = null; });
  on($("file"), "change", (e: Event) => {
    const inp = e.target as HTMLInputElement, f = inp.files?.[0]; if (!f) return;
    inp.value = ""; importFile(f);
  });
  on($("paste-go"), "click", () => {
    $("sheet-row").hidden = true;
    const text = $<HTMLTextAreaElement>("paste").value; if (text.trim()) openText(text, t("pasted text"));
  });
  on($("sample"), "click", async () => {
    const { default: csv } = await import("../../samples/dips-example.csv?raw");
    pushHistory(); applySample(P(), csv); X.selectedSet = -1; X.tablePage = 0;
    update(); hideLeft(true);
  });
  // drop a file anywhere, paste a table anywhere
  const box = $("netbox"); let depth = 0;
  const isFile = (e: DragEvent) => !!e.dataTransfer && [...e.dataTransfer.types].includes("Files");
  on(document, "dragenter", (e: DragEvent) => { if (!isFile(e)) return; e.preventDefault(); depth++; box.classList.add("dropping"); });
  on(document, "dragover", (e: DragEvent) => { if (isFile(e)) e.preventDefault(); });
  on(document, "dragleave", (e: DragEvent) => { if (!isFile(e)) return; if (--depth <= 0) { depth = 0; box.classList.remove("dropping"); } });
  on(document, "drop", (e: DragEvent) => {
    if (!isFile(e)) return; e.preventDefault(); depth = 0; box.classList.remove("dropping");
    const f = e.dataTransfer!.files[0]; if (!f) return;
    hideLeft(false); importFile(f);
  });
  on(document, "paste", (e: ClipboardEvent) => {
    const tag = (e.target as HTMLElement).tagName; if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    const text = e.clipboardData?.getData("text/plain") ?? ""; if (!/\d/.test(text) || text.split(/\r?\n/).length < 2) return;
    e.preventDefault(); hideLeft(false); $<HTMLTextAreaElement>("paste").value = text; $("sheet-row").hidden = true; openText(text, t("pasted text"));
  });
}
